import $ = require('jquery');

interface FormData {
    [name: string]: string;
}

type FormResponse = FormSuccessResponse | FormErrorResponse;

interface FormSuccessResponse {
    status: 'success';
}

interface FormErrorResponse {
    status: 'error';
    errors: FormData;
}

enum SendButtonState {
    Send,
    Sending,
    Sent
}

const FORM_SELECTOR = "form.vcard-contact-form";
const CAPTCHA_FIELD_NAME = "captcha";
const CAPTCHA_SELECTOR = ".captcha";
const CAPTCHA_REFRESH_SELECTOR = "a.refresh";
const BUTTON_SENT_CLASS = "sent";
const FIELD_ERROR_CLASS = "field-error";
const FIELD_ERROR_MESSAGE_CLASS = "field-error-message";
const SUBMIT_FIELD_NAME = "submit";

class ContactForm {
    /** Timeout Id for resetting the send button to initial state after form successfully sent */
    private resetSendButtonStateId: number;

    init() {
        let formEl = this.getFormEl();
        if (!formEl) {
            return;
        }

        formEl.addEventListener('submit', (e) => this.onSubmit(e));
        this.getFields().forEach(f => {
            f.addEventListener('focus', () => this.clearFieldError(f.getAttribute('name')));
        });
    }

    private refreshCaptcha() {
        let captchaEl = this.getFormEl().querySelector(CAPTCHA_SELECTOR);
        captchaEl.querySelector(CAPTCHA_REFRESH_SELECTOR).dispatchEvent(new MouseEvent('click'));
        this.clearField(CAPTCHA_FIELD_NAME);
    }

    private onSubmit(e: Event) {
        // Prevent normal sending of the form
        e.preventDefault();

        let data = this.collectFormData();
        let url = this.getFormUrl();

        // Clear field errors whenever we start sending the form
        this.clearErrors();

        this.setSendButtonState(SendButtonState.Sending);

        this.sendForm(url, data)
            .then((response) => this.handleResponse(response));
    }

    private getSendButtonEl() {
        return this.getFormEl().querySelector('button[name="submit"]') as HTMLButtonElement;
    }

    /**
     * Updates the state of the send button
     *
     * The state button changes to a "Sending..." text when clicked
     * When the response is received, the button is temporarily set to
     * "Sent" for a few seconds and then reverted to its original state.
     */
    private setSendButtonState(state: SendButtonState) {
        // If it's not yet reset to original state, but it's pending, cancel it
        if (this.resetSendButtonStateId) {
            clearTimeout(this.resetSendButtonStateId);
            this.resetSendButtonStateId = void 0;
        }

        // Queue setting the state back to original after showing the "Sent" text for some time
        if (state === SendButtonState.Sent) {
            this.resetSendButtonStateId = setTimeout(
                () => this.setSendButtonState(SendButtonState.Send),
                6000
            );
        }

        let buttonEl = this.getSendButtonEl();
        buttonEl.disabled = state === SendButtonState.Sending;

        let labels = new Map<SendButtonState, string>()
            .set(SendButtonState.Send, buttonEl.getAttribute('data-send'))
            .set(SendButtonState.Sending, buttonEl.getAttribute('data-sending'))
            .set(SendButtonState.Sent, buttonEl.getAttribute('data-sent'));

        buttonEl.innerHTML = labels.get(state);

        if (state === SendButtonState.Sent) {
            buttonEl.classList.add(BUTTON_SENT_CLASS);
            $(".success-confirmation-message").removeClass('fadeOut').addClass('fadeIn');
        } else {
            buttonEl.classList.remove(BUTTON_SENT_CLASS);
            if (state === SendButtonState.Send && $(".success-confirmation-message").hasClass('fadeIn')) {
                $(".success-confirmation-message").removeClass('fadeIn').addClass('fadeOut');
            }
        }
    }

    private handleResponse(response: FormResponse) {
        this.refreshCaptcha();

        if (response.status === 'error') {
            this.handleError(response.errors);
        } else {
            this.handleSuccess();
        }
    }

    private handleError(errors: FormData) {
        for (let fieldName in errors) {
            if (errors.hasOwnProperty(fieldName)) {
                this.setFieldError(fieldName, errors[fieldName]);
            }
        }

        this.setSendButtonState(SendButtonState.Send);
    }

    private setFieldError(fieldName: string, errorMessage: string) {
        let fieldEl = this.getFieldElement(fieldName);
        if (fieldName !== SUBMIT_FIELD_NAME) {
            fieldEl.classList.add(FIELD_ERROR_CLASS);
        }
        let errorEl = document.createElement('div');
        errorEl.classList.add(FIELD_ERROR_MESSAGE_CLASS);
        errorEl.innerHTML = errorMessage;

        let targetEl = this.getFormEl();
        let refChild: Node;
        if (fieldName === CAPTCHA_FIELD_NAME) {
            refChild = this.getSendButtonEl().parentNode;
        } else {
            refChild = fieldEl.nextSibling;
        }
        targetEl.insertBefore(errorEl, refChild);
    }

    private clearFieldError(fieldName: string) {
        let fieldEl = this.getFieldElement(fieldName);
        fieldEl.classList.remove(FIELD_ERROR_CLASS);
        let fieldErrorEl = this.getErrorFieldEl(fieldName);
        if (fieldErrorEl) {
            fieldErrorEl.remove();
        }
    }

    private getErrorFieldEl(fieldName: string) {
        // We need to target the message element based on the field to which it is "attached"
        let messageAnchorSelector: string;
        if (fieldName === CAPTCHA_FIELD_NAME) {
            messageAnchorSelector = CAPTCHA_SELECTOR;
        } else {
            messageAnchorSelector = `[name="${fieldName}"]`;
        }

        return this.getFormEl().querySelector(
            `${messageAnchorSelector} + .${FIELD_ERROR_MESSAGE_CLASS}`
        );
    }

    private clearErrors() {
        this.getFields().forEach(f => this.clearFieldError(f.getAttribute('name')));
        this.clearFieldError(SUBMIT_FIELD_NAME);
    }

    private clearField(fieldName: string) {
        let field = this.getFields().find(f => f.getAttribute('name') === fieldName);
        field.value = "";
    }

    private clearFields() {
        this.getFields().forEach(f => f.value = "");
    }

    private handleSuccess() {
        this.clearErrors();
        this.clearFields();
        this.setSendButtonState(SendButtonState.Sent);
    }

    private sendForm(url: string, data: FormData) {
        return Promise.resolve<string>($.ajax({
            url,
            data,
            method: "POST"
        })).then(r => JSON.parse(r) as FormResponse);
    }

    private getFormEl() {
        return document.querySelector(FORM_SELECTOR);
    }

    private getFieldElement(fieldName: string) {
        return this.getFormEl().querySelector(`[name="${fieldName}"]`);
    }

    private getFormUrl() {
        return this.getFormEl().getAttribute('action');
    }

    private getFields() {
        return Array.from(
            this.getFormEl().querySelectorAll('input[type="text"], textarea')
        ) as (HTMLInputElement|HTMLTextAreaElement)[];
    }

    private collectFormData() {
        let fields = this.getFields();

        let postData: FormData = {};
        for (let field of fields) {
            postData[field.getAttribute('name')] = field.value;
        }
        return postData;
    }
}

export = new ContactForm;



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/vcard/contactForm-web.ts
 **/